import { FC } from 'react';
import type { SeopScenario } from '@/types/seop';

/** A última vez que alguém, nesta tela, tornou um cenário o plano atual. */
export interface RestauracaoRecente {
  /** Nome do cenário que virou o plano atual. */
  nome: string;
  /** O mês do plano que mudou (o do cenário); vazio em backend antigo. */
  mes: string;
  /** O snapshot do plano anterior — é ele que o Desfazer restaura. */
  backup: SeopScenario;
  /** O que não veio ou ficou como estava (`ressalvasDaRestauracao`). */
  ressalvas: string[];
}

interface FaixaDoPlanoProps {
  /** Cenário salvo aberto para leitura; `null` = a tela mostra o plano atual. */
  aberto: SeopScenario | null;
  /** O cenário mais recente que alguém salvou (backups automáticos não contam). */
  ultimoSalvo: SeopScenario | null;
  /** Mês do seletor de período — é com ele que o cenário aberto é calculado. */
  mes: string;
  /** Mês que estava selecionado quando o cenário aberto foi salvo. */
  mesDoCenario: string;
  /** O cenário aberto não tem o mês do seletor (não existia quando ele foi salvo). */
  semMes: boolean;
  restauracao: RestauracaoRecente | null;
  ocupado: boolean;
  onVoltar: () => void;
  onRestaurar: () => void;
  onDesfazer: () => void;
}

const quando = (iso: string): string => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const BOTAO = 'rounded-lg border border-theme-border px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary disabled:opacity-50';

/** Logo depois de trocar o plano: o que aconteceu, o que ficou de fora e o caminho de volta. */
const AvisoDeRestauracaoFeita: FC<{ restauracao: RestauracaoRecente; ocupado: boolean; onDesfazer: () => void }> = ({
  restauracao,
  ocupado,
  onDesfazer,
}) => (
  <div
    data-testid="seop-restauracao-feita"
    role="status"
    className="mb-4 flex flex-wrap items-start gap-3 rounded-xl border border-theme-success/40 bg-theme-success/10 p-4 text-sm text-content-secondary"
  >
    <span aria-hidden className="material-symbols-outlined text-theme-success">
      published_with_changes
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-content-primary">
        "{restauracao.nome}" agora é o plano atual{restauracao.mes && ` de ${restauracao.mes}`}
      </p>
      <p>
        Vale para toda a equipe{restauracao.mes && '; os outros meses do plano não mudaram'}. O plano que estava antes
        ficou guardado em "{restauracao.backup.name}".
      </p>
      {restauracao.ressalvas.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs" data-testid="seop-ressalvas">
          {restauracao.ressalvas.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </div>
    <button type="button" data-testid="seop-desfazer" onClick={onDesfazer} disabled={ocupado} className={`${BOTAO} font-semibold text-content-primary`}>
      Desfazer
    </button>
  </div>
);

const PlanoAtual: FC<{ ultimoSalvo: SeopScenario | null }> = ({ ultimoSalvo }) => (
  <div
    data-testid="seop-faixa-plano"
    className="mb-4 flex items-start gap-3 rounded-xl border border-theme-border bg-surface-card p-4 text-sm text-content-secondary"
  >
    <span aria-hidden className="material-symbols-outlined text-accent">
      edit_note
    </span>
    <div>
      <p className="font-semibold text-content-primary">Você está no plano atual</p>
      <p>
        É o plano em que a equipe trabalha: o que for salvo nas abas Demanda, Balanceamento e Capacidade vale para
        todos. Um cenário salvo é uma foto dele com nome — abrir uma foto para olhar não muda o plano atual.
      </p>
      <p className="mt-1 text-xs text-content-muted">
        {ultimoSalvo
          ? `Último cenário salvo: "${ultimoSalvo.name}", em ${quando(ultimoSalvo.created_at)}.`
          : 'Nenhum cenário salvo ainda: salve o plano atual abaixo para ter um ponto de volta.'}
      </p>
    </div>
  </div>
);

const CenarioAberto: FC<
  Pick<FaixaDoPlanoProps, 'mes' | 'mesDoCenario' | 'semMes' | 'ocupado' | 'onVoltar' | 'onRestaurar'> & { aberto: SeopScenario }
> = ({
  aberto,
  mes,
  mesDoCenario,
  semMes,
  ocupado,
  onVoltar,
  onRestaurar,
}) => (
  <div
    data-testid="seop-faixa-cenario-aberto"
    role="status"
    className="mb-4 flex flex-wrap items-start gap-3 rounded-xl border border-accent bg-accent/10 p-4 text-sm text-content-secondary"
  >
    <span aria-hidden className="material-symbols-outlined text-accent">
      visibility
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-content-primary">Você está vendo o cenário salvo "{aberto.name}" (somente leitura)</p>
      <p>
        Salvo por {aberto.created_by_name || 'alguém da equipe'} em {quando(aberto.created_at)}. O plano atual não foi
        alterado: os números abaixo são os da foto, calculados para {mes || 'o mês selecionado'}.
      </p>
      {semMes ? (
        <p className="mt-1 font-semibold text-content-primary" data-testid="seop-cenario-sem-mes">
          Este cenário não tem dados de {mes}: ele foi salvo para {mesDoCenario}. Troque o mês no seletor de período.
        </p>
      ) : (
        mesDoCenario !== mes && (
          <p className="mt-1 text-xs text-content-muted">
            Este cenário foi salvo com {mesDoCenario} selecionado; troque o mês no seletor para ver aquele período.
          </p>
        )
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" data-testid="seop-voltar-plano" onClick={onVoltar} className={`${BOTAO} font-semibold text-content-primary`}>
        Voltar ao plano atual
      </button>
      <button type="button" data-testid="seop-restaurar-aberto" onClick={onRestaurar} disabled={ocupado} className={BOTAO}>
        Tornar este o plano atual…
      </button>
    </div>
  </div>
);

/**
 * Diz, sempre, o que a pessoa está olhando: o plano atual ou a foto de um cenário salvo.
 *
 * Nasceu do feedback do Herrison (out/26): a tela só oferecia "Restaurar", que sobrescreve,
 * e ninguém sabia se abrir um cenário antigo custaria o trabalho em curso. Abrir é leitura;
 * a faixa repete isso enquanto o cenário estiver aberto e dá o caminho de volta. Depois de
 * tornar um cenário o plano atual, ela mostra o que aconteceu e o Desfazer.
 */
const FaixaDoPlano: FC<FaixaDoPlanoProps> = ({ aberto, ultimoSalvo, restauracao, ocupado, onDesfazer, ...resto }) => {
  if (aberto) return <CenarioAberto aberto={aberto} ocupado={ocupado} {...resto} />;
  return (
    <>
      {restauracao && <AvisoDeRestauracaoFeita restauracao={restauracao} ocupado={ocupado} onDesfazer={onDesfazer} />}
      <PlanoAtual ultimoSalvo={ultimoSalvo} />
    </>
  );
};

export default FaixaDoPlano;
